/**
 * VoucherRecipientsTab — v4.45
 *
 * Guest-Recipient management inside the Voucher Control Center.
 * Distinct from the existing "allocation recipients" concept
 * (employees / cards that hold a voucher pool). Guest Recipients
 * are end-users who received a voucher instance via a customer-
 * facing flow — currently only Loyal Review.
 *
 * Data source: `GET /api/vouchers/{voucher_id}/guest-recipients`
 * (see backend/routes/voucher_guest_recipients.py).
 *
 * Default filter: "This Voucher" (the currently selected source
 * configuration). Toggle to "All Vouchers" to see every recipient
 * the caller is authorised for.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Users, Search, Download, Copy, RefreshCcw, Filter, ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_BASE = window.location.origin;

const STATUS_VARIANT = {
    issued: 'default',
    redeemed: 'default',
    pending: 'secondary',
    expired: 'destructive',
    cancelled: 'destructive',
};

const formatDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch (_) { return iso; }
};

/**
 * Props:
 *  - voucherConfigs: list of source voucher configurations
 *      (each must expose `id` and a human label field).
 *  - organizations, allLocations: for filter dropdowns.
 *  - token: bearer for authenticated fetch.
 */
export default function VoucherRecipientsTab({
    voucherConfigs = [], organizations = [], allLocations = [], token,
    deepLink = null, onDeepLinkConsumed,
}) {
    const { t } = useTranslation();
    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    // Scope: "this voucher" (default) vs "all". If there's only one
    // configured voucher, default to that specific id; otherwise
    // default to the first configured voucher.
    const defaultVoucherId = voucherConfigs.length > 0 ? voucherConfigs[0].id : 'all';
    const [scope, setScope] = useState('this');
    const [selectedVoucherId, setSelectedVoucherId] = useState(defaultVoucherId);
    const [statusFilter, setStatusFilter] = useState('all');
    const [orgFilter, setOrgFilter] = useState('all');
    const [locationFilter, setLocationFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('date_issued');
    const [sortOrder, setSortOrder] = useState('desc');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [copiedAllAt, setCopiedAllAt] = useState(0);
    // v4.45.1 — Deep-link row highlight state. When set, the row with
    // this interaction_id gets a temporary accent + is scrolled into
    // view. Auto-clears after 2500ms.
    const [highlightedInteractionId, setHighlightedInteractionId] = useState(null);
    const rowRefs = useRef({});
    const deepLinkAppliedRef = useRef(false);

    // v4.45.1 — Apply the deep-link once per navigation:
    //   1. Force scope to "this" and select the source voucher.
    //   2. Preserve the interaction id so we can scroll + highlight
    //      the matching row after rows load. Falls back gracefully to
    //      the tab with the correct voucher pre-selected when the
    //      interaction id is stale/deleted.
    useEffect(() => {
        if (!deepLink || deepLinkAppliedRef.current) return;
        if (deepLink.sourceId) {
            setScope('this');
            setSelectedVoucherId(deepLink.sourceId);
        } else if (deepLink.voucherCode) {
            // No source id available — fall back to "All Vouchers" and
            // pre-filter by the code so the row still appears.
            setScope('all');
            setSearch(deepLink.voucherCode);
        }
        if (deepLink.interactionId) {
            setHighlightedInteractionId(deepLink.interactionId);
        }
        deepLinkAppliedRef.current = true;
        // Clear the query string once consumed so a refresh doesn't
        // re-trigger the highlight, and the URL stays clean.
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('interaction_id');
            url.searchParams.delete('voucher_source_id');
            url.searchParams.delete('voucher_code');
            window.history.replaceState({}, '', url.toString());
        } catch (_) { /* no-op */ }
        if (onDeepLinkConsumed) onDeepLinkConsumed();
    }, [deepLink, onDeepLinkConsumed]);

    const effectiveVoucherId = scope === 'all' ? 'all' : (selectedVoucherId || 'all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
            if (orgFilter && orgFilter !== 'all') params.set('org_id', orgFilter);
            if (locationFilter && locationFilter !== 'all') params.set('location_id', locationFilter);
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            if (search) params.set('search', search);
            params.set('sort_by', sortBy);
            params.set('sort_order', sortOrder);
            params.set('limit', '500');
            const res = await fetch(
                `${API_BASE}/api/vouchers/${encodeURIComponent(effectiveVoucherId)}/guest-recipients?${params.toString()}`,
                { headers },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(t('vouchers.recipients.loadFailed', 'Failed to load recipients'));
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [effectiveVoucherId, statusFilter, orgFilter, locationFilter, dateFrom, dateTo, search, sortBy, sortOrder, headers, t]);

    useEffect(() => { load(); }, [load]);

    // v4.45.1 — After rows load, scroll the deep-linked row into view
    // and drop the highlight state after ~2.5s. If the row is not
    // found (deleted/archived), we silently no-op — no alerts, no
    // error messages, per the enterprise-grade UX directive.
    useEffect(() => {
        if (!highlightedInteractionId || rows.length === 0) return;
        // Wait one paint so the row DOM has settled.
        const timer = setTimeout(() => {
            const el = rowRefs.current[highlightedInteractionId];
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            // Clear the highlight after the accent fades.
            const clearTimer = setTimeout(() => setHighlightedInteractionId(null), 2500);
            return () => clearTimeout(clearTimer);
        }, 100);
        return () => clearTimeout(timer);
    }, [highlightedInteractionId, rows]);

    const copyAllEmails = async () => {
        const emails = rows.map(r => r.email).filter(Boolean).join('\n');
        if (!emails) {
            toast.info(t('vouchers.recipients.noEmailsToCopy', 'No emails to copy'));
            return;
        }
        try {
            await navigator.clipboard.writeText(emails);
        } catch (_) {
            const ta = document.createElement('textarea');
            ta.value = emails;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); } catch (_) { /* ignore */ }
            document.body.removeChild(ta);
        }
        setCopiedAllAt(Date.now());
        toast.success(t('vouchers.recipients.emailsCopied', '{{n}} emails copied', { n: rows.filter(r => r.email).length }));
        setTimeout(() => setCopiedAllAt(0), 2000);
    };

    const exportFile = (format) => {
        const params = new URLSearchParams();
        if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
        if (orgFilter && orgFilter !== 'all') params.set('org_id', orgFilter);
        if (locationFilter && locationFilter !== 'all') params.set('location_id', locationFilter);
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);
        if (search) params.set('search', search);
        params.set('sort_by', sortBy);
        params.set('sort_order', sortOrder);
        params.set('format', format);
        const url = `${API_BASE}/api/vouchers/${encodeURIComponent(effectiveVoucherId)}/guest-recipients/export?${params.toString()}`;
        // Use fetch → blob so we can attach the Authorization header
        // (a plain <a href> wouldn't carry the bearer token).
        fetch(url, { headers })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.blob();
            })
            .then(blob => {
                const objectUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = `voucher-recipients.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            })
            .catch(() => toast.error(t('vouchers.recipients.exportFailed', 'Export failed')));
    };

    const clearFilters = () => {
        setStatusFilter('all'); setOrgFilter('all'); setLocationFilter('all');
        setDateFrom(''); setDateTo(''); setSearch('');
    };

    return (
        <div className="space-y-4" data-testid="voucher-recipients-tab">
            {/* Scope toggle */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {t('vouchers.recipients.title', 'Recipients')}
                        {total > 0 && (
                            <span className="text-xs font-normal text-muted-foreground">
                                ({total})
                            </span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <Select value={scope} onValueChange={setScope}>
                            <SelectTrigger className="w-[180px]" data-testid="voucher-recipients-scope">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="this">{t('vouchers.recipients.thisVoucher', 'This Voucher')}</SelectItem>
                                <SelectItem value="all">{t('vouchers.recipients.allVouchers', 'All Vouchers')}</SelectItem>
                            </SelectContent>
                        </Select>
                        {scope === 'this' && voucherConfigs.length > 0 && (
                            <Select value={selectedVoucherId} onValueChange={setSelectedVoucherId}>
                                <SelectTrigger className="w-[260px]" data-testid="voucher-recipients-source-picker">
                                    <SelectValue placeholder={t('vouchers.recipients.pickVoucher', 'Pick voucher configuration')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {voucherConfigs.map(v => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.label || `$${v.value || '—'} ${v.reason_code || ''}`.trim()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="voucher-recipients-refresh">
                            <RefreshCcw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                            {t('common.refresh', 'Refresh')}
                        </Button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-2 items-center">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('vouchers.recipients.searchPlaceholder', 'Search email or code…')}
                                className="pl-8"
                                data-testid="voucher-recipients-search"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[150px]" data-testid="voucher-recipients-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('vouchers.recipients.statusAll', 'All Status')}</SelectItem>
                                <SelectItem value="issued">{t('reviewShield.voucherStatus.issued', 'Issued')}</SelectItem>
                                <SelectItem value="redeemed">{t('reviewShield.voucherStatus.redeemed', 'Redeemed')}</SelectItem>
                                <SelectItem value="expired">{t('reviewShield.voucherStatus.expired', 'Expired')}</SelectItem>
                                <SelectItem value="cancelled">{t('reviewShield.voucherStatus.cancelled', 'Cancelled')}</SelectItem>
                                <SelectItem value="pending">{t('reviewShield.voucherStatus.pending', 'Pending')}</SelectItem>
                            </SelectContent>
                        </Select>
                        {organizations.length > 1 && (
                            <Select value={orgFilter} onValueChange={setOrgFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="voucher-recipients-org">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('vouchers.recipients.allOrgs', 'All Organizations')}</SelectItem>
                                    {organizations.map(o => (
                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {allLocations.length > 1 && (
                            <Select value={locationFilter} onValueChange={setLocationFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="voucher-recipients-location">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('vouchers.recipients.allLocations', 'All Locations')}</SelectItem>
                                    {allLocations.map(l => (
                                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[160px]" data-testid="voucher-recipients-date-from" />
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[160px]" data-testid="voucher-recipients-date-to" />
                        <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="voucher-recipients-clear">
                            <Filter className="w-4 h-4 mr-1" />
                            {t('common.clear', 'Clear')}
                        </Button>
                    </div>

                    {/* Bulk actions */}
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={copyAllEmails}
                            disabled={!rows.some(r => r.email)}
                            data-testid="voucher-recipients-copy-emails"
                        >
                            <Copy className="w-4 h-4 mr-1" />
                            {copiedAllAt
                                ? `✓ ${t('vouchers.recipients.copiedAllEmails', 'Copied')}`
                                : t('vouchers.recipients.copyEmails', 'Copy Visible Emails')}
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="voucher-recipients-export">
                                    <Download className="w-4 h-4 mr-1" />
                                    {t('common.export', 'Export')}
                                    <ChevronDown className="w-3 h-3 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => exportFile('csv')} data-testid="voucher-recipients-export-csv">
                                    CSV (.csv)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportFile('xlsx')} data-testid="voucher-recipients-export-xlsx">
                                    Excel (.xlsx)
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    {/* Table */}
                    <div className="border rounded-md overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[220px]">{t('vouchers.recipients.email', 'Email')}</TableHead>
                                    {scope === 'all' && (
                                        <TableHead className="min-w-[140px]">{t('vouchers.recipients.voucher', 'Voucher')}</TableHead>
                                    )}
                                    <TableHead className="min-w-[120px]">{t('vouchers.recipients.code', 'Code')}</TableHead>
                                    <TableHead className="min-w-[80px]">{t('vouchers.recipients.rating', 'Rating')}</TableHead>
                                    <TableHead className="min-w-[120px]">{t('vouchers.recipients.experience', 'Experience')}</TableHead>
                                    <TableHead
                                        className="min-w-[140px] cursor-pointer"
                                        onClick={() => { setSortBy('date_issued'); setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'); }}
                                        data-testid="voucher-recipients-sort-issued"
                                    >
                                        {t('vouchers.recipients.dateIssued', 'Date Issued')} {sortBy === 'date_issued' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                                    </TableHead>
                                    <TableHead className="min-w-[100px]">{t('vouchers.recipients.status', 'Status')}</TableHead>
                                    <TableHead
                                        className="min-w-[140px] cursor-pointer"
                                        onClick={() => { setSortBy('date_redeemed'); setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'); }}
                                        data-testid="voucher-recipients-sort-redeemed"
                                    >
                                        {t('vouchers.recipients.dateRedeemed', 'Date Redeemed')} {sortBy === 'date_redeemed' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                                    </TableHead>
                                    <TableHead className="min-w-[150px]">{t('vouchers.recipients.organization', 'Organization')}</TableHead>
                                    <TableHead className="min-w-[150px]">{t('vouchers.recipients.location', 'Location')}</TableHead>
                                    <TableHead className="min-w-[200px]">{t('vouchers.recipients.interactionId', 'Interaction ID')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={scope === 'all' ? 11 : 10} className="text-center py-8 text-muted-foreground">
                                            {t('common.loading', 'Loading…')}
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={scope === 'all' ? 11 : 10} className="text-center py-8 text-muted-foreground">
                                            {t('vouchers.recipients.noRecipients', 'No recipients match these filters')}
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map((r, idx) => {
                                    const isHighlighted = highlightedInteractionId && r.interaction_id === highlightedInteractionId;
                                    return (
                                        <TableRow
                                            key={r.voucher_id || idx}
                                            data-testid={`voucher-recipient-row-${idx}`}
                                            ref={(el) => { if (el && r.interaction_id) rowRefs.current[r.interaction_id] = el; }}
                                            className={isHighlighted
                                                ? 'bg-emerald-100 dark:bg-emerald-900/40 ring-2 ring-emerald-400 transition-colors duration-500'
                                                : 'transition-colors duration-500'}
                                        >
                                        <TableCell className="font-mono text-xs">{r.email || '—'}</TableCell>
                                        {scope === 'all' && (
                                            <TableCell className="text-sm">{r.voucher_name || '—'}</TableCell>
                                        )}
                                        <TableCell className="font-mono text-xs">{r.voucher_code}</TableCell>
                                        <TableCell>{r.guest_rating != null ? `${r.guest_rating}★` : '—'}</TableCell>
                                        <TableCell className="text-xs capitalize">{r.experience_type || '—'}</TableCell>
                                        <TableCell className="text-xs">{formatDate(r.date_issued)}</TableCell>
                                        <TableCell>
                                            <Badge variant={STATUS_VARIANT[r.status] || 'secondary'} className="text-xs capitalize">
                                                {t(`reviewShield.voucherStatus.${r.status}`, r.status)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs">{formatDate(r.date_redeemed)}</TableCell>
                                        <TableCell className="text-xs">{r.organization_name || '—'}</TableCell>
                                        <TableCell className="text-xs">{r.location_name || '—'}</TableCell>
                                        <TableCell className="font-mono text-[10px] text-muted-foreground truncate max-w-[200px]" title={r.interaction_id || ''}>
                                            {r.interaction_id || '—'}
                                        </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
